/* eslint no-mixed-operators: 0 */

import React from 'react'
import MediaCarouselItem from './MediaCarouselItem'
import { direction as directionEnum, MediaCarouselProps, MediaCarouselState } from './types'
import Icon, { IconType } from '@bt-react/components/Icon'
import style from './MediaCarousel.module.scss'
import classnames from 'classnames'
import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'

class MediaCarousel extends React.Component<MediaCarouselProps, MediaCarouselState> {

  _ID = 'talks-media-carousel'
  _LIST_MARGIN = 20
  _MUTATION_DELAY= 900
  carouselRef = React.createRef<HTMLDivElement>()
  listRef = React.createRef<HTMLUListElement>()
  timeoutHandler

  constructor (props) {

    super(props)
    this.state = {
      carouselWidth: 0,
      itemElements: [],
      itemsPerPage: 0,
      itemWidth: 0,
      resizeListenerSet: false,
      translateValue: 0,
      bias: directionEnum.PREVIOUS
    }
    this.setCarouselWidth = this.setCarouselWidth.bind(this)

  }

  /**
   * Calculate the number of items that can be wholly visible in the carousel per page
   * */
  getItemsPerPage (): number {

    if (this.state.itemWidth === 0 || this.state.carouselWidth === 0) return 0
    return Math.floor(this.state.carouselWidth / this.state.itemWidth)

  }

  get pageOffset (): number {

    return this.state.itemsPerPage * this.state.itemWidth

  }

  get totalWidth (): number {

    return this.state.itemElements.length * this.state.itemWidth

  }

  get maxOffset (): number {

    return this.totalWidth - this.state.carouselWidth - this._LIST_MARGIN

  }

  componentDidMount () {

    this.setItems(this.props.items as any, this.props.selected)
    this.addListeners()

  }

  componentDidUpdate (prevProps: MediaCarouselProps, prevState: MediaCarouselState) {

    const hasPropChanged = (prop): boolean => prevProps[prop] !== this.props[prop]
    const hasStateChanged = (...props: string[]): boolean =>
      props.some(prop => prevState[prop] !== this.state[prop])

    if (hasPropChanged('isSidePanelOpen')) this.handleMutation()

    if (this.carouselRef.current && this.state.carouselWidth === 0) this.setCarouselWidth()

    if (this.listRef.current && this.state.itemWidth === 0) this.setItemWidth()

    /** If the carousel is now hidden, reset the view to show the selected item */
    if (hasPropChanged('hidden') && !this.props.hidden) this.centreItem(this.props.selected)

    if (hasPropChanged('items') || hasPropChanged('selected')) {

      this.setItems(this.props.items as any, this.props.selected)
      this.centreItem(this.props.selected)

    }
    /**
     * Set/change vars if an essential prop changes
     */
    if (
      this.state.carouselWidth > 0 &&
      this.state.itemWidth > 0 &&
      this.state.itemElements.length > 0 ||
      hasStateChanged('itemElements', 'carouselWidth')
    ) {

      /** Now that carousel width and item width are set, fetch pages and items per page */
      this.setCarouselVars()

    }

    /**
     * Deal With Navigation
     */

    if (this.state.itemElements.length) {

      const firstItemObscured = Math.abs(this.state.translateValue) < this.state.itemWidth &&
        this.state.translateValue !== 0 &&
        this.state.bias === directionEnum.PREVIOUS

      const paginationRequired = this.state.translateValue < 0 && this.state.itemElements.length > this.state.itemsPerPage

      if (this.state.itemElements.length <= this.state.itemsPerPage) {

        throttle(this.centreContent, 200)()

      }
      else if (this.props.selected !== prevProps.selected) {

        this.setBias(this.props.selected > prevProps.selected ? directionEnum.NEXT : directionEnum.PREVIOUS)
        this.centreItem(this.props.selected)

      }
      else if (firstItemObscured || paginationRequired) {

        this.pinToLeft()

      }
      else if (this.state.translateValue > this.maxOffset && this.maxOffset > 0) {

        this.pinToRight()

      }

    }

  }

  componentWillUnmount () {

    this.removeListeners()

  }

  addListeners = () => {

    window.addEventListener('resize', this.handleMutation)

  }

  removeListeners = () => {

    try {

      window.removeEventListener('resize', this.handleMutation)

    }
    catch (e) {

    }

  }

  handleMutation = debounce(() => {

    this.setCarouselWidth()

  }, this._MUTATION_DELAY)

  /** Fetches the carousel width from the DOM */
  setCarouselWidth (): void {

    if (!this.carouselRef.current) return
    const { clientWidth } = this.carouselRef.current
    if (this.state.carouselWidth === clientWidth) return
    this.setState({ carouselWidth: clientWidth || 0 })

  }

  centreItem = index => {

    const centre = (this.state.carouselWidth - this.state.itemWidth) / 2
    const itemPosition = index * this.state.itemWidth
    const translateValue = Math.max(0, itemPosition - centre)

    this.setState({ translateValue })

  }

  setBias (direction) {

    if (this.state.bias !== direction) this.setState({ bias: direction })

  }

  pinToLeft () {

    this.setState({ translateValue: 0 })

  }

  pinToRight () {

    this.setState({ translateValue: this.maxOffset })

  }

  centreContent = () => {

    const translateValue = -(this.state.carouselWidth - this.totalWidth) / 2
    if (translateValue !== this.state.translateValue) this.setState({ translateValue })

  }

  setCarouselVars = () => {

    const itemsPerPage = this.getItemsPerPage()
    if (itemsPerPage !== this.state.itemsPerPage) this.setState({ itemsPerPage })

  }

  /** Generate the JSX Element array */
  setItems (items: React.JSX.Element[] = [], selected = -1): void {

    const itemElements: React.JSX.Element[] = items
      .map((ele, i): React.JSX.Element => {

        return <MediaCarouselItem
          content={ele}
          dataBdd={`${this._ID}-item`}
          key={`${this._ID}-item-${i}`}
          data-item={`${this._ID}-item-${i}`}
          selected={selected === i}
        />

      }
      )

    this.setState({ itemElements })

  }

  /** Set the width of an item based on the first child in the JSX Element array */
  setItemWidth (): void {

    if (!this.listRef.current) return
    if (this.listRef.current.childElementCount === 0) return

    const ele: HTMLLIElement = this.listRef.current.children[0] as HTMLLIElement

    this.setState({ itemWidth: ele.offsetWidth + this._LIST_MARGIN })

  }

  /** Change the current page */
  changePage = (direction: directionEnum): void => {

    const target = this.state.translateValue + (this.pageOffset * direction)

    const translateValue = Math.max(0, Math.min(target, this.maxOffset))

    if (translateValue !== this.state.translateValue) return this.setState({ translateValue, bias: direction })

  }

  getCarouselContent = (): React.JSX.Element => {

    if (this.state.itemElements.length > 0) {

      return <ul
        className={style[`List`]}
        data-bdd={`${this._ID}-list`}
        id={`${this._ID}-list`}
        ref={this.listRef}
        style={{ transform: `translateX(${-this.state.translateValue}px)` }}
      >
        {this.state.itemElements}
      </ul>

    }

    if (this.props.empty) {

      return <div
        className={style[`MediaCarousel-Empty`]}
        data-bdd={`${this._ID}-empty-container`}
      >
        <Icon
          className={style[`MediaCarousel-EmptyIcon`]}
          colour={IconType.colour.tertiary}
          dataBdd={`${this._ID}-empty-icon`}
          name={this.props.empty.icon}
        />
        <span
          className={classnames(style[`MediaCarousel-EmptyMessage`], style['heading-text-400'])}
          data-bdd={`${this._ID}-empty-message`} >
          {this.props.empty.message}
        </span>
      </div>

    }

    return <></>

  }

  render () {

    const prevButtonDisabled = this.state.translateValue <= 0
    const nextButtonDisabled = this.state.translateValue >= this.maxOffset

    return <div
      className={style['MediaCarousel']}
      data-bdd={this._ID}
      id={this._ID}
    >
      <div className={style['MediaCarousel-Nav-Button-Container']}>
        <button
          type='button'
          onClick={() => this.changePage(directionEnum.PREVIOUS)}
          disabled={prevButtonDisabled}
          className={
            classnames(style['MediaCarousel-Nav-Button'], {
              [style['MediaCarousel-Nav-Button--disabled']]: prevButtonDisabled
            })
          }
          data-bdd={`${this._ID}-prev-button`}
        >
          <Icon name={IconType.iconName.angleLeft} />
        </button>
      </div>
      <div
        className={style['MediaCarousel-Container']}
        data-bdd={`${this._ID}-container`}
        id={`${this._ID}-container`}
        ref={this.carouselRef}
      >
        {
          this.getCarouselContent()
        }
      </div>
      <div className={style['MediaCarousel-Nav-Button-Container']}>
        <button
          type='button'
          onClick={() => this.changePage(directionEnum.NEXT)}
          disabled={nextButtonDisabled}
          className={
            classnames(style['MediaCarousel-Nav-Button'], {
              [style['MediaCarousel-Nav-Button--disabled']]: nextButtonDisabled
            })
          }
          data-bdd={`${this._ID}-next-button`}
        >
          <Icon name={IconType.iconName.angleRight} />
        </button>
      </div>
    </div>

  }

}

export default MediaCarousel
